import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { recordTypes, statuses } from "@/apis/equipment";
import { EquipmentEnumItem } from "@/types/equipment";

interface EquipmentEnumsContextValue {
  statusItems: EquipmentEnumItem[]; // 设备状态枚举
  recordTypeItems: EquipmentEnumItem[]; // 设备记录类型枚举
}

const EquipmentEnumsContext = createContext<
  EquipmentEnumsContextValue | undefined
>(undefined);

// 设备枚举 hooks，方便在组件中直接使用
export const useEquipmentEnumsContext = () => {
  const context = useContext(EquipmentEnumsContext);
  if (!context) {
    throw new Error(
      "Equipment enum hooks must be used within EquipmentEnumsProvider",
    );
  }
  return context;
};

// 设备枚举 Provider，进入设备管理页面时统一加载共享枚举
export const EquipmentEnumsProvider = ({ children }: PropsWithChildren) => {
  const [statusItems, setStatusItems] = useState<EquipmentEnumItem[]>([]); // 设备状态枚举
  const [recordTypeItems, setRecordTypeItems] = useState<EquipmentEnumItem[]>(
    [],
  ); // 设备记录类型枚举

  useEffect(() => {
    // 两个请求共用取消控制器，页面卸载时统一中止
    const controller = new AbortController();

    // 获取设备状态枚举
    statuses(controller)
      .then((items) => {
        if (!controller.signal.aborted) {
          setStatusItems(items ?? []);
        }
      })
      .catch(() => undefined);

    // 获取设备记录类型枚举
    recordTypes(controller)
      .then((items) => {
        if (!controller.signal.aborted) {
          setRecordTypeItems(items ?? []);
        }
      })
      .catch(() => undefined);

    // 离开设备管理页面时取消尚未完成的请求
    return () => controller.abort();
  }, []);

  return (
    <EquipmentEnumsContext.Provider value={{ statusItems, recordTypeItems }}>
      {children}
    </EquipmentEnumsContext.Provider>
  );
};
